/**
 * Liste der Kunden bzw. Lieferanten.
 *
 * Dieselbe Seite bedient beide Rollen - die Daten liegen in einer
 * Tabelle, und ein Betrieb kann gleichzeitig Kunde und Lieferant sein.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api, withQuery } from '../api/client';
import type { ListResponse, Partner } from '../api/types';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { Badge, EmptyState, LoadingBlock, TextInput } from '../components/ui';

export function PartnersPage({ role }: { role: 'customer' | 'supplier' }) {
  const navigate = useNavigate();
  const { can } = useAuth();

  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const label = role === 'customer' ? 'Kunden' : 'Lieferanten';

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['partners', role, search, showInactive, offset],
    queryFn: () =>
      api.get<ListResponse<Partner>>(
        withQuery('/api/partners', {
          role,
          q: search || undefined,
          active: showInactive ? 'all' : 'true',
          limit,
          offset,
        }),
      ),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title={label}
        subtitle={data ? `${data.total} ${data.total === 1 ? 'Eintrag' : 'Einträge'}` : undefined}
        actions={
          can('masterdata:write') ? (
            <Link className="btn btn-primary" to={`/${role === 'customer' ? 'kunden' : 'lieferanten'}/neu`}>
              Neu anlegen
            </Link>
          ) : null
        }
      />

      <div className="card">
        <div className="table-toolbar">
          <TextInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setOffset(0);
            }}
            placeholder="Suche nach Name, Nummer, Ort oder E-Mail …"
          />
          <label className="checkbox-row" style={{ fontSize: 13 }}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(event) => {
                setShowInactive(event.target.checked);
                setOffset(0);
              }}
            />
            Inaktive anzeigen
          </label>
        </div>

        {isLoading ? <LoadingBlock /> : null}

        {isError ? (
          <div className="table-empty">{(error as Error).message}</div>
        ) : null}

        {data && !isLoading ? (
          data.items.length === 0 ? (
            <EmptyState
              title={search ? 'Keine Treffer' : `Noch keine ${label} erfasst`}
              hint={search ? 'Bitte ändern Sie den Suchbegriff.' : undefined}
            />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="shrink">Nummer</th>
                    <th>Name</th>
                    <th>Ort</th>
                    <th>E-Mail</th>
                    <th className="shrink">USt-IdNr.</th>
                    <th className="shrink">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((partner) => (
                    <tr
                      key={partner.id}
                      className="clickable"
                      onClick={() =>
                        navigate(`/${role === 'customer' ? 'kunden' : 'lieferanten'}/${partner.id}`)
                      }
                    >
                      <td className="mono">{partner.number}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{partner.name}</div>
                        {partner.name2 ? <div className="faint">{partner.name2}</div> : null}
                      </td>
                      <td>
                        {partner.postalCode ? `${partner.postalCode} ` : ''}
                        {partner.city ?? ''}
                      </td>
                      <td className="muted">{partner.email ?? ''}</td>
                      <td className="mono faint">{partner.vatId ?? ''}</td>
                      <td>
                        {partner.active ? (
                          <div className="row" style={{ gap: 4 }}>
                            {partner.isCustomer ? <Badge kind="accent">Kunde</Badge> : null}
                            {partner.isSupplier ? <Badge>Lieferant</Badge> : null}
                            {partner.lockedAt ? (
                              <span title={partner.lockReason ?? 'Satzsperre'}>
                                <Badge kind="danger">Gesperrt</Badge>
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <Badge kind="warning">Inaktiv</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}

        {data && data.total > limit ? (
          <div className="table-footer">
            <span>
              {offset + 1} – {Math.min(offset + limit, data.total)} von {data.total}
            </span>
            <div className="row">
              <button
                type="button"
                className="btn btn-sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
              >
                Zurück
              </button>
              <button
                type="button"
                className="btn btn-sm"
                disabled={offset + limit >= data.total}
                onClick={() => setOffset(offset + limit)}
              >
                Weiter
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
